import Skeleton from "@components/skeleton/Skeleton";
import React from "react";
import { WithTranslation, withTranslation } from "react-i18next";

import { IconSize, Status } from "../../../enums";
import { KeyName } from "../../../keyName";
import { IAuditTrailData } from "../../../model/Model";
import TestIds from "../../../testIds";
import { getTabIndex } from "../../auditTrail/Utils";
import BusyIndicator from "../../busyIndicator";
import { BusyIndicatorSize, BusyIndicatorType } from "../../busyIndicator/BusyIndicator.utils";
import { HOTSPOT_ID_ATTR } from "../../hotspots/Hotspots.utils";
import { ColoredIcon } from "../../icon";
import { CheckIcon, CloseIcon, IProps as IIconProps } from "../../icon/Icon";
import { FIELD_CONTENT_PADDING } from "../field/Field.styles";
import { IValueInputComponentProps } from "../input";
import { InputStatusStraight } from "../input/Input.styles";
import {
    AuditTrailLayer,
    Base,
    Body,
    Circle,
    InnerCircle,
    InnerLabel,
    InnerSpan,
    Label,
    StyledSwitch
} from "./Switch.styles";

export interface ISwitchIconSet {
    on: React.FunctionComponent<IIconProps>;
    off: React.FunctionComponent<IIconProps>;
}

export interface IProps extends IValueInputComponentProps, WithTranslation {
    checked?: boolean;
    // if switch should indicate its state by semantic color, like on/off green/red
    hasSemanticColor?: boolean;
    // light variant could be rendered on dark backgrounds, like in filter
    isLight?: boolean;
    label?: string;
    onChange?: (checked: boolean) => void;
    type?: SwitchType;
    isBusy?: boolean;
    isSkeleton?: boolean;
    iconSet?: ISwitchIconSet;
    hotspotId?: string;
    auditTrailData?: IAuditTrailData;
    name?: string;

    style?: React.CSSProperties;
    className?: string;
}

export enum SwitchType {
    Default = "Default",
    YesNo = "YesNo",
    Icons = "Icons",
    OnOff = "OnOff"
}

class Switch extends React.PureComponent<IProps> {
    static defaultProps = {
        isLight: false,
        type: SwitchType.Default,
        hasSemanticColor: false,
        checked: false
    };

    handleClick = (/*e: MouseEvent<HTMLElement>*/) => {
        this.select();
    };

    handleKeyDown = (e: React.KeyboardEvent) => {
        if ([KeyName.Space, KeyName.Enter].includes(e.key as KeyName)) {
            e.preventDefault();
            this.select();
        }
    };

    select = () => {
        if (!this.isDisabledOrReadOnly() && !this.props.isBusy && !this.props.auditTrailData) {
            this.props.onChange?.(!this.props.checked);
        }
    };

    getIconWithProps = (Icon: React.FunctionComponent<IIconProps>) => {
        const size = IconSize.S;
        const style = { position: "relative", top: "50%", transform: "translateY(-50%)" } as React.CSSProperties;
        return <ColoredIcon><Icon style={style} width={size} height={size}/></ColoredIcon>;
    };

    getPositiveLabel = () => {
        switch (this.props.type) {
            case SwitchType.Icons:
                return this.getIconWithProps(this.props.iconSet?.on ?? CheckIcon);
            case SwitchType.YesNo:
                return this.props.t("Common:General.Yes");
            case SwitchType.OnOff:
                return this.props.t("Common:General.On");
            default:
                return null;
        }
    };

    getNegativeLabel = () => {
        switch (this.props.type) {
            case SwitchType.Icons:
                return this.getIconWithProps(this.props.iconSet?.off ?? CloseIcon);
            case SwitchType.YesNo:
                return this.props.t("Common:General.No");
            case SwitchType.OnOff:
                return this.props.t("Common:General.Off");
            default:
                return null;
        }
    };

    isDisabledOrReadOnly = () => {
        return this.props.isDisabled || this.props.isReadOnly;
    };

    render() {
        if (this.props.isSkeleton) {
            return <SwitchSkeleton/>;
        }

        const { auditTrailData } = this.props;
        const _isAuditTrail = !!auditTrailData;

        const positiveLabel = this.getPositiveLabel(),
            negativeLabel = this.getNegativeLabel();
        const switchStyleProps = {
            _isChecked: this.props.checked,
            _isLight: this.props.isLight,
            _isSemantic: this.props.hasSemanticColor
        };

        return (
            <StyledSwitch onClick={this.handleClick} onKeyDown={this.handleKeyDown}
                          isDisabled={this.props.isDisabled}
                          isReadOnly={this.props.isReadOnly || _isAuditTrail}
                          style={this.props.style}
                          className={this.props.className}
                          {...{ [HOTSPOT_ID_ATTR]: this.props.hotspotId }}
                          data-testid={TestIds.Switch}>
                {this.props.label &&
                    <Label auditTrailData={auditTrailData} isLight={this.props.isLight}
                           data-testid={TestIds.FieldLabel}>
                        <InnerSpan auditTrailData={auditTrailData}>{this.props.label}</InnerSpan>
                    </Label>}
                <Body role="switch"
                      isReadOnly={this.props.isReadOnly}
                      isDisabled={this.props.isDisabled}
                      isAuditTrail={_isAuditTrail}
                      _isLight={this.props.isLight}
                      tabIndex={this.isDisabledOrReadOnly() ? null : getTabIndex(this.props)}
                      aria-checked={this.props.checked}
                      data-testid={TestIds.SwitchBody}
                      data-name={this.props.name}
                      aria-disabled={this.isDisabledOrReadOnly() ? this.isDisabledOrReadOnly() : null}
                >
                    <AuditTrailLayer
                        auditTrailData={auditTrailData}
                        data-testid={TestIds.AuditTrailLayer}
                        _isChecked={this.props.checked}/>
                    <Base {...switchStyleProps}
                          _hasIcon={this.props.type === SwitchType.Icons}
                          _isWithoutHover={this.props.isBusy || this.isDisabledOrReadOnly() || _isAuditTrail}>
                        {positiveLabel &&
                            <InnerLabel {...switchStyleProps} _isPositive={true}>
                                {positiveLabel}
                            </InnerLabel>}
                        {negativeLabel &&
                            <InnerLabel {...switchStyleProps} _isPositive={false}>
                                {negativeLabel}
                            </InnerLabel>}
                        <Circle {...switchStyleProps}
                                data-testid={TestIds.SwitchCircle}
                                _isWithoutHover={this.props.isBusy || this.isDisabledOrReadOnly() || _isAuditTrail}>
                            {!this.props.isBusy
                                ? <InnerCircle {...switchStyleProps} />
                                :
                                <BusyIndicator size={BusyIndicatorSize.XS} type={BusyIndicatorType.WithoutBackground}/>
                            }
                        </Circle>
                    </Base>
                </Body>
                {!this.props.isReadOnly && this.props.showChange &&
                    <InputStatusStraight left={`-${FIELD_CONTENT_PADDING}px`} status={Status.Warning}/>}
            </StyledSwitch>
        );
    }
}

export default withTranslation(["Common"])(Switch);

const SwitchSkeleton: React.FunctionComponent = () => {
    return (
        <div>
            <div style={{ display: "flex", gap: "14px", width: "100%", alignItems: "center" }}>
                <Skeleton width={"120px"} height={"16px"}/>
                <Skeleton width={"74px"} height={"32px"} borderRadius={"16px"}/>
            </div>
        </div>
    );
};